import { SMap } from '@client/new/helpers/types';
import { FieldErrors } from 'react-hook-form';
import { ISection } from './types';

/**
 * Maps section types to human-readable titles
 */
const sectionTitlesMap: Partial<Record<ISection['type'], string>> = {
  Bio: 'Bio',
  TextSection: 'Headline + text',
  TextSection1: 'Headline + text + media',
  TextSection2: 'Headline + text + 2 images',
  PaidProgramsList: 'Paid programs carousel',
  ProgramsList: 'Subscription programs carousel',
  ProgramsHighlight: 'Featured program',
  Features: 'Features',
  FeaturedBenefit: 'Featured benefit',
  HeroFullImage: 'Hero image',
  FeaturesNew: 'App features',
  HeroFullVideo: 'Hero video',
  FAQs: 'FAQs',
  Reviews: 'Reviews',
  Testimonials: 'Testimonials',
  Introduction: 'Hero with benefits list',
  Logos: 'Publishers',
  EmailCapture: 'Email Capture',
  FeaturedOffer: 'Featured offer',
  OffersCarousel: 'Offers carousel',
};

/**
 * Converts a section to a human-readable title
 */
const toSectionTitle = (
  section: Pick<ISection, 'type' | 'enabled'>,
): string => {
  let title = sectionTitlesMap[section.type] || section.type;

  if (section.enabled === false) {
    title = `Disabled - ${title}`;
  }

  return title;
};

/**
 * Converts form validation errors to a format suitable for the Errors component
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toErrors = <T extends SMap<any>>(
  sections: ISection[],
  e: FieldErrors<T>,
) => {
  if (!e.sections || !Array.isArray(e.sections)) return null;

  const messages: { names: string[]; msg: string }[] = [];

  e.sections.forEach((v, i) => {
    if (!v || i >= sections.length) return;

    const title = toSectionTitle(sections[i]);

    Object.keys(v).forEach((k) => {
      const error = v[k];
      if (!error?.message) return;

      const names = [`#sections.${i}`, `sections.${i}.${k}`];
      const msg = `Section ${title}: ${error.message}`;

      // Avoid duplicate messages
      if (messages.find((m) => m.msg === msg)) return;

      messages.push({ msg, names });
    });
  });

  if (messages.length === 0) return null;

  return {
    title:
      messages.length > 1 ? 'There are several problems' : 'There is a problem',
    messages,
  };
};
